import type { GroupSession } from './groupTypes';

interface GroupCapacityBadgeProps {
  session: GroupSession;
  compact?: boolean;
}

function capacityColor(session: GroupSession): string {
  if (!session.isOpen) return '#94a3b8';
  const ratio = session.enrolledCount / session.capacity;
  if (ratio >= 1) return '#ef4444';    // full
  if (ratio >= 0.8) return '#f97316';  // nearly full
  if (ratio >= 0.5) return '#eab308';  // filling up
  return '#22c55e';                     // open
}

function capacityLabel(session: GroupSession): string {
  if (!session.isOpen) return 'Closed';
  if (session.enrolledCount >= session.capacity) return 'Full';
  if (session.enrolledCount / session.capacity >= 0.8) return 'Nearly full';
  return 'Open';
}

export function GroupCapacityBadge({ session, compact = false }: GroupCapacityBadgeProps) {
  const color = capacityColor(session);
  const label = capacityLabel(session);
  const spotsLeft = Math.max(0, session.capacity - session.enrolledCount);

  if (compact) {
    return (
      <span
        title={`Group: ${session.enrolledCount}/${session.capacity} enrolled · ${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left${session.waitlistCount > 0 ? ` · ${session.waitlistCount} waitlisted` : ''}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          fontSize: 10,
          fontWeight: 700,
          color,
          background: `${color}18`,
          border: `1px solid ${color}40`,
          borderRadius: 4,
          padding: '1px 5px',
          lineHeight: 1.5,
          whiteSpace: 'nowrap',
        }}
      >
        <GroupIcon size={9} color={color} />
        {session.enrolledCount}/{session.capacity}
      </span>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px',
        background: `${color}10`,
        border: `1px solid ${color}30`,
        borderRadius: 6,
        fontSize: 11,
      }}
    >
      <GroupIcon size={12} color={color} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontWeight: 700, color, lineHeight: 1 }}>
          {session.groupName}
          {' '}
          <span style={{ fontWeight: 400, color: '#64748b' }}>· {label}</span>
        </span>
        <span style={{ color: '#64748b', lineHeight: 1 }}>
          {session.enrolledCount}/{session.capacity} enrolled
          {session.waitlistCount > 0 && ` · ${session.waitlistCount} waitlisted`}
        </span>
      </div>
    </div>
  );
}

function GroupIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

// ─── Capacity fill-bar for the roster panel ────────────────────────────────

export function CapacityBar({ session }: { session: GroupSession }) {
  const pct = Math.min(100, Math.round((session.enrolledCount / session.capacity) * 100));
  const color = capacityColor(session);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div
        style={{
          flex: 1,
          height: 6,
          background: '#e2e8f0',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: color,
            borderRadius: 3,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color, whiteSpace: 'nowrap', minWidth: 44, textAlign: 'right' }}>
        {session.enrolledCount}/{session.capacity}
      </span>
    </div>
  );
}
